import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button, ButtonGroup, Icon, Spinner, Stack } from "@shopify/polaris"
import { EditMinor, ViewMinor, DuplicateMinor } from "@shopify/polaris-icons"
import { ExternalHelp } from "components/widgets/CollectPage/forms/fields/CollectFields"
import { useEntity } from "hooks/useAxios"
import AiServicesEntity from "services/entities/AiServicesEntity"
import TableOrganism from "views/organisms/tables/TableOrganism"
import useFieldIsLocked from "../../../fields/useFieldIsLocked"
import ModalAiService from "views/molecules/collects/ai_inventories/ModalAiService"

const AiServiceTableLine = ({
  aiServices,
  providersWithModels,
  refreshData,
  reloadEnvCollect,
  canAddService,
  t,
}) => {
  const { isLocked } = useFieldIsLocked()
  const aiServicesEntity = useEntity(AiServicesEntity)
  const [duplicatingId, setDuplicatingId] = useState(null)

  const getProviderDisplayName = (name) => {
    if (!providersWithModels || !name) return name || "-"
    return providersWithModels[name]?.display_name || name
  }

  const getModelDisplayName = (providerName, modelName) => {
    if (!providersWithModels || !modelName) return modelName || "-"
    const models = providersWithModels[providerName]?.models || []
    return models.find((m) => m.name === modelName)?.display_name || modelName
  }

  const handlePostAction = () => {
    refreshData()
    reloadEnvCollect?.()
  }

  const handleDuplicate = async (serviceId) => {
    try {
      setDuplicatingId(serviceId)
      await aiServicesEntity.duplicateAiService(serviceId)
      handlePostAction()
    } catch (error) {
      console.error("Error duplicating AI service:", error)
    } finally {
      setDuplicatingId(null)
    }
  }

  const hasServices = (aiServices || []).length > 0

  const tableStructure = [
    {
      header: t("collect:questions.13.ai_services.table.project_name"),
      type: "text",
      display: (service) => service.project_name || "-",
    },
    {
      header: t("collect:questions.13.ai_services.table.use_case_name"),
      type: "text",
      display: (service) => service.use_case_name || "-",
    },
    {
      header: t("collect:questions.13.ai_services.table.provider"),
      type: "text",
      display: (service) => getProviderDisplayName(service.provider),
    },
    {
      header: t("collect:questions.13.ai_services.table.model"),
      type: "text",
      display: (service) =>
        getModelDisplayName(service.provider, service.model),
    },
    {
      header: t(
        "collect:questions.13.ai_services.table.output_completion_tokens"
      ),
      type: "numeric",
      display: (service) => service.output_completion_tokens ?? "-",
    },
    {
      header: !isLocked && canAddService ? (
        <ModalAiService postAction={handlePostAction} iconOnly />
      ) : (
        ""
      ),
      type: "text",
      display: (service) => (
        <ButtonGroup segmented key={service.id}>
          <ModalAiService aiService={service} postAction={handlePostAction}>
            <Button plain>
              <Icon source={isLocked ? ViewMinor : EditMinor} />
            </Button>
          </ModalAiService>
          {!isLocked && (
            <Button
              plain
              onClick={() => handleDuplicate(service.id)}
              disabled={duplicatingId === service.id}
              title={t("collect:questions.13.ai_services.table.duplicate")}
            >
              {duplicatingId === service.id ? (
                <Spinner size="small" />
              ) : (
                <Icon source={DuplicateMinor} />
              )}
            </Button>
          )}
        </ButtonGroup>
      ),
    },
  ]

  return (
    <div>
      <Stack vertical>
        <TableOrganism
          tableStructure={tableStructure}
          data={hasServices ? aiServices : []}
        />
        {!hasServices && (
          <Stack distribution="center" alignment="center" spacing="extraTight">
            <p>{t("collect:questions.13.ai_services.table.empty")}</p>
            {!isLocked && canAddService && (
              <ModalAiService postAction={handlePostAction} iconOnly />
            )}
          </Stack>
        )}
      </Stack>
      <ExternalHelp>
        {t("collect:questions.13.ai_services.table.help")}
      </ExternalHelp>
    </div>
  )
}

AiServiceTableLine.propTypes = {
  aiServices: PropTypes.array,
  providersWithModels: PropTypes.object,
  refreshData: PropTypes.func.isRequired,
  reloadEnvCollect: PropTypes.func,
  canAddService: PropTypes.bool,
  t: PropTypes.func.isRequired,
}

export default AiServiceTableLine
